import { useEffect, useRef, useState } from 'react';
import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';
import { addClasses } from '../../../../../utils/eventListeners';
import { authAPI } from '../../../../../services/redwolfSdkAPI';
import { openNotification } from '../../../../../store/commonSlice';
import { notification } from '../../../../../utils/constants';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { checkNameFormat, isAlphaNumeric, isNumeric } from '../../../../../utils/validations';
import { getToken } from '../../../../../utils/localStorage';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';

interface RegisterProductModalProps {
	isProductRegisterInfoModalOpen: boolean;
	getRegisterProductList: () => void;
	handleModalRegisterClose: () => void;
}

const RegisterProduct = ({
	isProductRegisterInfoModalOpen,
	getRegisterProductList,
	handleModalRegisterClose
}: RegisterProductModalProps) => {
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
	const [formErrorMessages, setFormErrorMessages] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: '',
		zipCode: '',
		serialNumber: ''
	});
	const [productRegistrationForm, setProductRegistrationForm] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: '',
		zipCode: '',
		serialNumber: ''
	});
	const dispatch = useDispatch();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();

	const firstNameRef = useRef<HTMLInputElement>(null);
	const lastNameRef = useRef<HTMLInputElement>(null);
	const phoneNumberRef = useRef<HTMLInputElement>(null);
	const zipCodeRef = useRef<HTMLInputElement>(null);
	const serialNumberRef = useRef<HTMLInputElement>(null);
	const [requestRedwolfService] = useRequestRedwolfService();
	const [isShowHelpDetails, setIsShowHelpDetails] = useState(false);
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Register Product' });

	useEffect(() => {
		addClasses();
	}, []);

	useEffect(() => {
		if (
			productRegistrationForm.firstName &&
			productRegistrationForm.lastName &&
			productRegistrationForm.phoneNumber.length === 10 &&
			productRegistrationForm.zipCode.length === 5 &&
			productRegistrationForm.serialNumber.length >= 14
		) {
			setIsSaveButtonDisabled(false);
		} else {
			setIsSaveButtonDisabled(true);
		}
	}, [
		productRegistrationForm.firstName,
		productRegistrationForm.lastName,
		productRegistrationForm.phoneNumber.length,
		productRegistrationForm.serialNumber.length,
		productRegistrationForm.zipCode.length
	]);

	const handleOnBlur = (event: any) => {
		const { value, name } = event.target;

		if (name === 'firstName') {
			if (value === '') {
				setFormErrorMessages({ ...formErrorMessages, firstName: 'This is a required field' });
			}
		} else if (name === 'lastName') {
			if (value === '') {
				setFormErrorMessages({ ...formErrorMessages, lastName: 'This is a required field' });
			}
		} else if (name === 'phoneNumber') {
			if (value === '') {
				setFormErrorMessages({ ...formErrorMessages, phoneNumber: 'This is a required field' });
			} else if (value.length < 10) {
				setFormErrorMessages({
					...formErrorMessages,
					phoneNumber: 'Please enter a valid phone number'
				});
			}
		} else if (name === 'zipCode') {
			if (value === '') {
				setFormErrorMessages({ ...formErrorMessages, zipCode: 'This is a required field' });
			} else if (value.length < 5) {
				setFormErrorMessages({ ...formErrorMessages, zipCode: 'Please enter a valid ZIP Code' });
			}
		} else if (name === 'serialNumber') {
			if (value === '') {
				setFormErrorMessages({ ...formErrorMessages, serialNumber: 'This is a required field' });
			} else if (value.length < 14) {
				setFormErrorMessages({
					...formErrorMessages,
					serialNumber: 'Please enter a valid serial number'
				});
			}
		}
	};

	const updateRegisteredProducts = () => {
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_REGISTER_PRODUCT');

		requestRedwolfService(
			authAPI.updateRegisteredProducts(token, [productRegistrationForm.serialNumber])
		)
			.then((response: any) => {
				setIsSpinnerLoading(false);
				if (response.constructor.$metadata$.simpleName === 'Success') {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_REGISTER_PRODUCT_SUCCESS');
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.addProductSuccess
						})
					);
					getRegisterProductList();
					handleModalClose();
				} else {
					showTechnicalErrorMessage();
					Sentry.captureException(new Error('productRegister:' + response));
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_REGISTER_PRODUCT_FAILURE', {
						error: response.constructor.$metadata$.simpleName
					});
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_REGISTER_PRODUCT_FAILURE', { error: err });
			});
	};

	const handleSubmit = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);

		requestRedwolfService(authAPI.validateSerialNumber(token, productRegistrationForm.serialNumber))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					updateRegisteredProducts();
				} else {
					setIsSpinnerLoading(false);
					if (response.response && response.response.status === 404) {
						setIsSaveButtonDisabled(true);
						setFormErrorMessages({
							...formErrorMessages,
							serialNumber:
								'Your serial number doesn’t exist. Please check and enter a valid serial number.'
						});
						if (serialNumberRef.current) {
							serialNumberRef.current.focus();
						}
					} else {
						showTechnicalErrorMessage();
						Sentry.captureException(new Error('validateSerialNumber:' + response));
					}
				}
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleKeyDown = (event: any) => {
		const { name } = event.target;

		if ((name === 'firstName' || name === 'lastName') && !checkNameFormat(event.key)) {
			event.preventDefault();
		}
	};

	const handleInputChange = (event: any) => {
		const { value, name } = event.target;

		setFormErrorMessages({ ...formErrorMessages, [name]: '' });

		if (name === 'phoneNumber' || name === 'zipCode') {
			if (!value) {
				setProductRegistrationForm({ ...productRegistrationForm, [name]: '' });
			}
			if (isNumeric(value)) {
				setProductRegistrationForm({ ...productRegistrationForm, [name]: value });
			}
		} else if (name === 'serialNumber') {
			if (!value) {
				setProductRegistrationForm({ ...productRegistrationForm, [name]: '' });
			}
			if (isAlphaNumeric(value)) {
				setProductRegistrationForm({ ...productRegistrationForm, [name]: value });
			}
		} else {
			setProductRegistrationForm({ ...productRegistrationForm, [name]: value });
		}
	};

	const handleModalClose = () => {
		setFormErrorMessages({
			...formErrorMessages,
			firstName: '',
			lastName: '',
			phoneNumber: '',
			zipCode: '',
			serialNumber: ''
		});
		setProductRegistrationForm({
			...productRegistrationForm,
			firstName: '',
			lastName: '',
			phoneNumber: '',
			zipCode: '',
			serialNumber: ''
		});
		if (firstNameRef.current) {
			firstNameRef.current.classList.remove('active');
		}
		if (lastNameRef.current) {
			lastNameRef.current.classList.remove('active');
		}
		if (phoneNumberRef.current) {
			phoneNumberRef.current.classList.remove('active');
		}
		if (zipCodeRef.current) {
			zipCodeRef.current.classList.remove('active');
		}
		if (serialNumberRef.current) {
			serialNumberRef.current.classList.remove('active');
		}
		handleModalRegisterClose();
	};

	const handleShowNeedHelpContent = (event: any) => {
		event.preventDefault();
		setIsShowHelpDetails(!isShowHelpDetails);
	};

	return (
		<div className="sb-pr-component sub-purchase-comp reg-product">
			{/* Product Registration Modal */}
			<Modal
				containerId="registerNewCard"
				containerClasses="tv-product-reg-modal"
				show={isProductRegisterInfoModalOpen}
				onHide={() => handleModalClose()}
				heading="Product Registration">
				<form className="edit-modal-form" id="product-register-submit">
					<div className="form-field form-field-group">
						<div
							className={`field  half-filed reg-firstname ${
								formErrorMessages.firstName && 'has-error'
							}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control"
									id="first-name"
									name="firstName"
									value={productRegistrationForm.firstName}
									aria-describedby="firstName_req"
									aria-label="First Name"
									aria-required="true"
									ref={firstNameRef}
									onBlur={handleOnBlur}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
								/>
								<label htmlFor="first-name" className="control-label">
									First Name
								</label>
								<code id="firstName_req">{formErrorMessages.firstName}</code>
							</div>
						</div>
						<div
							className={`field half-filed reg-lastname ${
								formErrorMessages.lastName && 'has-error'
							}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control"
									id="last-name"
									name="lastName"
									aria-describedby="lastName_req"
									value={productRegistrationForm.lastName}
									aria-label="LAST NAME"
									aria-required="true"
									ref={lastNameRef}
									onBlur={handleOnBlur}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
								/>
								<label htmlFor="last-name" className="control-label">
									Last Name
								</label>
								<code id="lastName_req">{formErrorMessages.lastName}</code>
							</div>
						</div>
					</div>
					<div className="form-field form-field-group">
						<div
							className={`field half-filed reg-phone ${
								formErrorMessages.phoneNumber && 'has-error'
							}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control"
									id="phone-number"
									name="phoneNumber"
									value={productRegistrationForm.phoneNumber}
									maxLength={10}
									aria-describedby="phone_req"
									aria-label="PHONE NUMBER"
									aria-required="true"
									ref={phoneNumberRef}
									onBlur={handleOnBlur}
									onChange={handleInputChange}
								/>
								<label htmlFor="phone-number" className="control-label">
									Phone Number
								</label>
								<code id="phone_req">{formErrorMessages.phoneNumber}</code>
							</div>
						</div>

						<div
							className={`field half-filed reg-zipcode ${
								formErrorMessages.zipCode && 'has-error'
							}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control"
									id="zip-code"
									name="zipCode"
									maxLength={5}
									aria-describedby="zipcode_req"
									aria-label="ZIP Code"
									aria-required="true"
									ref={zipCodeRef}
									value={productRegistrationForm.zipCode}
									onBlur={handleOnBlur}
									onChange={handleInputChange}
								/>
								<label htmlFor="zip-code" className="control-label">
									ZIP Code
								</label>
								<code id="zipcode_req">{formErrorMessages.zipCode}</code>
							</div>
						</div>
					</div>
					<div
						className={`field reg-serialNumber ${formErrorMessages.serialNumber && 'has-error'}`}>
						<div className="input-box form-group input-label">
							<input
								type="text"
								className="form-control"
								id="serial-number"
								name="serialNumber"
								aria-describedby="serial_number_req"
								aria-label="Product Serial Number"
								aria-required="true"
								maxLength={17}
								ref={serialNumberRef}
								value={productRegistrationForm.serialNumber}
								onBlur={handleOnBlur}
								onChange={handleInputChange}
							/>
							<label htmlFor="serial-number" className="control-label">
								Product Serial Number
							</label>
							<code id="serial_number_req">{formErrorMessages.serialNumber}</code>
						</div>
					</div>
					<button
						id="needHelpBtn"
						className="need-help-btn"
						aria-label="Need Help to find the serial number of your product"
						aria-expanded={isShowHelpDetails ? 'true' : 'false'}
						onClick={handleShowNeedHelpContent}>
						Need Help?
						{!isShowHelpDetails ? (
							<span className="arrowalign ico-right"></span>
						) : (
							<span className="arrowalign ico-down"></span>
						)}
					</button>
					{isShowHelpDetails && (
						<div className="need-help-div" id="needHelpContent">
							<div className="need-help-logo">
								<img src="images/find-serial.png" alt="serial-number-location" />
							</div>
							<div className="need-help-content">
								<p>
									The serial number can be found on the back of the TV’s, on the side of the box,
									and sometimes on the receipt.
								</p>
								<p>
									The serial number can also be found by selecting “Menu”, “Admin &amp; Privacy”,
									then “System Information”.
								</p>
							</div>
						</div>
					)}
					<div className="form-field content-right">
						<button
							type="submit"
							id="prod-reg-btn"
							className="primary-button"
							disabled={isSaveButtonDisabled}
							onClick={handleSubmit}>
							Register
						</button>
					</div>
				</form>
			</Modal>
			{isSpinnerLoading && <Spinner />}
		</div>
	);
};

export default RegisterProduct;
